import { FC, useCallback } from "react";
import { Product, SortOrder } from "tm-shared/types";

import { FilterDrawerUI } from "@/domains/product/components";
import { useAppDispatch } from "@/hooks/redux";
import {
    patchProductsSelectedFilters,
    setProductsHideZero,
    setProductsSelectedFilters,
    setProductsShowOnlyMML,
    setProductsSortOrder,
    setProductsSortTag,
} from "@/store/slices/ui";

import { useOfflineFilteredProducts } from "./useOfflineFilteredProducts";

interface OfflineFilterDrawerProps {
    open: boolean;
    setOpen: (value: boolean) => void;
    products: Record<string, Product>;
    inventoryList?: {
        [barcode: string]: number;
    };
    mmlList?: Record<string, boolean>;
}

export const OfflineFilterDrawer: FC<OfflineFilterDrawerProps> = ({
    open,
    setOpen,
    products,
    inventoryList = {},
    mmlList,
}) => {
    const dispatch = useAppDispatch();
    const {
        availableFilters,
        availableProductsLength,
        hideZeroProducts,
        selectedFilters,
        showOnlyMML,
        sortOptions,
        sortOrder,
        sortTag,
    } = useOfflineFilteredProducts({
        products,
        inventoryList,
        mmlList,
    });

    const onClose = () => setOpen(false);

    const onToggleHideZeroProducts = () => {
        dispatch(setProductsHideZero(!hideZeroProducts));
    };

    const onToggleShowOnlyMML = () => {
        dispatch(setProductsShowOnlyMML(!showOnlyMML));
    };

    const handleFilterChange = useCallback(
        (tag: string, values: (string | number | boolean)[]) => {
            dispatch(patchProductsSelectedFilters({ [tag]: values }));
        },
        [dispatch],
    );

    const handleSortTagChange = useCallback(
        (tag: string | null) => {
            dispatch(setProductsSortTag(tag));
        },
        [dispatch],
    );

    const handleSortOrderChange = useCallback(
        (order: SortOrder) => {
            dispatch(setProductsSortOrder(order));
        },
        [dispatch],
    );

    const handleResetFilters = () => {
        dispatch(setProductsSelectedFilters({}));
        dispatch(setProductsSortOrder(SortOrder.Asc));
        dispatch(setProductsSortTag(null));
        onClose();
    };

    return (
        <FilterDrawerUI
            onClose={onClose}
            onApply={onClose}
            onReset={handleResetFilters}
            isOpen={open}
            products={products}
            availableFilters={availableFilters}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            availableProductsLength={availableProductsLength}
            hideZeroProducts={hideZeroProducts}
            onToggleHideZeroProducts={onToggleHideZeroProducts}
            showOnlyMML={mmlList ? showOnlyMML : undefined}
            onToggleShowOnlyMML={mmlList ? onToggleShowOnlyMML : undefined}
            sortConfig={{
                sortOrder,
                sortTag,
                sortOptions,
                onSortTagChange: handleSortTagChange,
                onSortOrderChange: handleSortOrderChange,
            }}
        />
    );
};
